import React, { ChangeEventHandler, FocusEventHandler, forwardRef, useState } from 'react';
import { identity, isNil } from 'lodash';
import classnames from 'classnames';
import Counter from '../Counter';
import classes from './index.module.scss';
import { TextareaProps } from './types';

/**
 * @deprecated Please use Textarea from @fiverr-private/forms instead.
 */
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
    children,
    disabled = false,
    showCounter,
    className,
    counterClassName: customCounterClassName,
    isSuccess = false,
    isError = false,
    maxLength,
    numberFormat = identity,
    value,
    onChange = Function.prototype,
    onFocus = Function.prototype,
    onBlur = Function.prototype,
    ...rest
}, ref) => {
    const [content, setContent] = useState<string>();
    const [focused, setFocused] = useState(false);

    const controlled = !isNil(value);

    const classNames = classnames(
        classes.textarea,
        {
            [classes.disabled]: disabled,
            [classes.focused]: focused,
            [classes.hasCounter]: showCounter,
            [classes.success]: isSuccess,
            [classes.error]: isError
        },
        className
    );

    const counterClassName = classnames(
        classes.counter,
        customCounterClassName
    );

    const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
        if (!controlled) {
            setContent(e.target.value);
        }

        onChange(e);
    };

    const handleFocus: FocusEventHandler<HTMLTextAreaElement> = (e) => {
        setFocused(true);
        onFocus(e);
    };

    const handleBlur: FocusEventHandler<HTMLTextAreaElement> = (e) => {
        setFocused(false);
        onBlur(e);
    };

    const truncateContent = (toTruncate: string | number = '') => {
        const toTruncateStr = toTruncate.toString();
        if (typeof maxLength === 'undefined') {
            return toTruncateStr;
        }
        return toTruncateStr.substring(0, maxLength);
    };

    const counterContent = truncateContent(
        content || children || value || ''
    );

    const props: TextareaProps = {
        disabled,
        maxLength,
        children,
        ...rest
    };

    if (controlled) {
        props.value = value;
    } else {
        props.defaultValue = props.defaultValue || content;
    }

    return (
        <div className={classNames}>
            <textarea {...props}
                ref={ref}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}/>
            {showCounter && <Counter className={counterClassName} current={counterContent.length} max={maxLength} format={numberFormat}/>}
        </div>
    );
});

Textarea.displayName = 'Textarea';

export default Textarea;
